import { useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';

// ─── Tipos ──────────────────────────────────────────────────────────────────────

/** Papéis aceitos — deve espelhar o enum do servidor */
export type ClicksignSignerRole = 'contractor' | 'contractee' | 'sign' | 'witness' | 'intervening' | 'receipt';

export interface ClicksignSigner {
  name: string;
  email: string;
  role: ClicksignSignerRole;
}

export interface SendToClicksignInput {
  diligencia_id: string;
  document_id: string;
  signers: ClicksignSigner[];
}

interface SendToClicksignResponse {
  success: boolean;
  envelope_id: string;
  clicksign_document_id: string;
  request_url: string | null;
  signers: Array<{ name: string; email: string }>;
  /** Indica que o documento já havia sido enviado anteriormente (envelope existente) */
  already_sent?: boolean;
}

// ─── Hook ───────────────────────────────────────────────────────────────────────

export const useClicksign = () => {
  const [isSending, setIsSending] = useState(false);
  const { toast } = useToast();

  /**
   * Envia um documento para assinatura via ClickSign.
   * Chama a Edge Function send-to-clicksign que executa o fluxo completo:
   * criar envelope → upload → signatarios → requisitos → ativar → notificar
   *
   * Se o documento já foi enviado anteriormente (idempotência), retorna
   * os dados do envelope existente com `already_sent: true`.
   */
  const sendToClicksign = async (
    input: SendToClicksignInput,
  ): Promise<SendToClicksignResponse | null> => {
    setIsSending(true);

    try {
      const { data, error } = await supabase.functions.invoke<SendToClicksignResponse>(
        'send-to-clicksign',
        { body: input },
      );

      if (error) {
        // Extrair mensagem real do corpo da resposta da Edge Function
        let detail = '';
        try {
          const ctx = (error as { context?: unknown }).context;
          if (ctx && typeof ctx === 'object' && 'json' in ctx && typeof (ctx as Response).json === 'function') {
            const body = await (ctx as Response).json();
            detail = body?.error || '';
          }
        } catch {
          // Ignora erro ao parsear contexto
        }
        throw new Error(detail || error.message);
      }

      if (!data?.success) {
        throw new Error((data as unknown as { error?: string })?.error || 'Erro desconhecido');
      }

      // Mensagem diferenciada para envelope já existente vs novo
      if (data.already_sent) {
        toast({
          title: 'Documento ja enviado',
          description: 'Este documento ja possui um envelope ativo no ClickSign. Utilize o link existente para assinatura.',
        });
      } else {
        toast({
          title: 'Documento enviado para assinatura',
          description: 'Envelope ClickSign criado. Os signatarios serao notificados por email.',
        });
      }

      return data;
    } catch (err) {
      console.error('Erro ao enviar para ClickSign:', err);

      const message = err instanceof Error ? err.message : 'Erro ao enviar para ClickSign';
      toast({
        title: 'Erro no envio',
        description: message,
        variant: 'destructive',
      });

      return null;
    } finally {
      setIsSending(false);
    }
  };

  return { sendToClicksign, isSending };
};
